import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const EMPTY_FORM = {
  name: '',
  address: '',
  latitude: '',
  longitude: '',
  radius_meters: '100',
  is_active: true,
};

const DEFAULT_CENTER = [-98.35, 39.5]; // [lng, lat] for MapLibre
const DEFAULT_ZOOM = 4;
const PINNED_ZOOM = 16;
const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const RADIUS_SOURCE_ID = 'checkin-radius';
const RADIUS_FILL_LAYER_ID = 'checkin-radius-fill';
const RADIUS_LINE_LAYER_ID = 'checkin-radius-line';

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatDistance = meters => {
  if (meters == null) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
};

const parseCoord = value => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

const createGeoJSONCircle = (lng, lat, radiusMeters, points = 64) => {
  const coords = [];
  const distanceX = radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));
  const distanceY = radiusMeters / 110540;

  for (let i = 0; i < points; i += 1) {
    const theta = (i / points) * (2 * Math.PI);
    coords.push([lng + distanceX * Math.cos(theta), lat + distanceY * Math.sin(theta)]);
  }
  coords.push(coords[0]);

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
  };
};

const ensureRadiusLayers = map => {
  if (!map.getSource(RADIUS_SOURCE_ID)) {
    map.addSource(RADIUS_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!map.getLayer(RADIUS_FILL_LAYER_ID)) {
    map.addLayer({
      id: RADIUS_FILL_LAYER_ID,
      type: 'fill',
      source: RADIUS_SOURCE_ID,
      paint: {
        'fill-color': '#00C8FF',
        'fill-opacity': 0.15,
      },
    });
  }
  if (!map.getLayer(RADIUS_LINE_LAYER_ID)) {
    map.addLayer({
      id: RADIUS_LINE_LAYER_ID,
      type: 'line',
      source: RADIUS_SOURCE_ID,
      paint: {
        'line-color': '#00C8FF',
        'line-width': 2,
      },
    });
  }
};

const setRadiusCircle = (map, lng, lat, radiusMeters) => {
  ensureRadiusLayers(map);
  const source = map.getSource(RADIUS_SOURCE_ID);
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: [createGeoJSONCircle(lng, lat, Math.max(1, radiusMeters || 100))],
  });
};

const clearRadiusCircle = map => {
  const source = map?.getSource?.(RADIUS_SOURCE_ID);
  if (!source) return;
  source.setData({ type: 'FeatureCollection', features: [] });
};

const SuperuserCheckInLocations = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [checkInsLoading, setCheckInsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const mapElRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const formRef = useRef(form);
  const setFormRef = useRef(setForm);

  formRef.current = form;
  setFormRef.current = setForm;

  const loadLocations = useCallback(async () => {
    const data = await apiService.getCheckInLocationsSuperuser();
    setLocations(Array.isArray(data?.locations) ? data.locations : []);
  }, []);

  const loadCheckIns = useCallback(async locationId => {
    if (!locationId) {
      setCheckIns([]);
      return;
    }
    setCheckInsLoading(true);
    try {
      const data = await apiService.getCheckInsSuperuser({
        location_id: locationId,
        success: true,
        page_size: 100,
      });
      setCheckIns(Array.isArray(data?.check_ins) ? data.check_ins : []);
    } catch (err) {
      console.error('Failed to load check-ins:', err);
      setError('Failed to load check-ins for this location.');
      setCheckIns([]);
    } finally {
      setCheckInsLoading(false);
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        await loadLocations();
      } catch (err) {
        console.error('Failed to load check-in locations:', err);
        setError('Failed to load check-in locations.');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [loadLocations, navigate]);

  useEffect(() => {
    loadCheckIns(selectedLocationId);
  }, [selectedLocationId, loadCheckIns]);

  const clearMapPin = useCallback(() => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (mapInstanceRef.current) {
      clearRadiusCircle(mapInstanceRef.current);
    }
  }, []);

  const placePin = useCallback((lat, lng, radiusMeters, { fly = false } = {}) => {
    const map = mapInstanceRef.current;
    if (!map || !window.maplibregl) return;

    const radius = Math.max(1, Number(radiusMeters) || 100);

    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    } else {
      const marker = new window.maplibregl.Marker({
        color: '#00C8FF',
        draggable: true,
      })
        .setLngLat([lng, lat])
        .addTo(map);

      marker.on('drag', () => {
        const pos = marker.getLngLat();
        if (map.isStyleLoaded()) {
          setRadiusCircle(
            map,
            pos.lng,
            pos.lat,
            parseCoord(formRef.current.radius_meters) || 100
          );
        }
      });

      marker.on('dragend', () => {
        const pos = marker.getLngLat();
        setFormRef.current(prev => ({
          ...prev,
          latitude: pos.lat.toFixed(7),
          longitude: pos.lng.toFixed(7),
        }));
        if (map.isStyleLoaded()) {
          setRadiusCircle(
            map,
            pos.lng,
            pos.lat,
            parseCoord(formRef.current.radius_meters) || 100
          );
        }
      });

      markerRef.current = marker;
    }

    if (map.isStyleLoaded()) {
      setRadiusCircle(map, lng, lat, radius);
    } else {
      map.once('load', () => setRadiusCircle(map, lng, lat, radius));
    }

    if (fly) {
      map.flyTo({ center: [lng, lat], zoom: PINNED_ZOOM });
    }
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    let timer;
    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      if (!window.maplibregl || !mapElRef.current) {
        timer = setTimeout(init, 100);
        return;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.resize();
        return;
      }

      const map = new window.maplibregl.Map({
        container: mapElRef.current,
        style: OPENFREEMAP_STYLE,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });

      map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      map.on('load', () => {
        ensureRadiusLayers(map);
        const lat = parseCoord(formRef.current.latitude);
        const lng = parseCoord(formRef.current.longitude);
        if (lat != null && lng != null) {
          placePin(lat, lng, parseCoord(formRef.current.radius_meters) || 100);
        }
      });

      map.on('click', event => {
        const { lng, lat } = event.lngLat;
        const radius = parseCoord(formRef.current.radius_meters) || 100;
        setFormRef.current(prev => ({
          ...prev,
          latitude: lat.toFixed(7),
          longitude: lng.toFixed(7),
        }));
        placePin(lat, lng, radius);
      });

      mapInstanceRef.current = map;
      setTimeout(() => map.resize(), 50);
    };

    init();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [loading, placePin]);

  useEffect(() => {
    const lat = parseCoord(form.latitude);
    const lng = parseCoord(form.longitude);
    const radius = parseCoord(form.radius_meters) || 100;

    if (lat == null || lng == null) {
      clearMapPin();
      return;
    }

    placePin(lat, lng, radius);
  }, [form.latitude, form.longitude, form.radius_meters, placePin, clearMapPin]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    clearMapPin();
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    }
  };

  const startEdit = location => {
    const lat = parseCoord(location.latitude);
    const lng = parseCoord(location.longitude);
    setEditingId(location.id);
    setForm({
      name: location.name || '',
      address: location.address || '',
      latitude: String(location.latitude ?? ''),
      longitude: String(location.longitude ?? ''),
      radius_meters: String(location.radius_meters ?? 100),
      is_active: !!location.is_active,
    });
    setError('');
    setNotice('');
    if (lat != null && lng != null) {
      placePin(lat, lng, location.radius_meters || 100, { fly: true });
    }
  };

  const handleSubmit = async () => {
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        latitude: form.latitude,
        longitude: form.longitude,
        radius_meters: Number(form.radius_meters),
        is_active: form.is_active,
      };

      if (editingId) {
        await apiService.updateCheckInLocationSuperuser(editingId, payload);
        setNotice('Location updated.');
      } else {
        await apiService.createCheckInLocationSuperuser(payload);
        setNotice('Location created.');
      }

      resetForm();
      await loadLocations();
    } catch (err) {
      const data = err?.response?.data;
      const message =
        data?.name?.[0] ||
        data?.latitude?.[0] ||
        data?.longitude?.[0] ||
        data?.radius_meters?.[0] ||
        data?.error ||
        'Failed to save location.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async location => {
    setBusyId(location.id);
    setError('');
    try {
      await apiService.updateCheckInLocationSuperuser(location.id, {
        name: location.name,
        address: location.address || '',
        latitude: location.latitude,
        longitude: location.longitude,
        radius_meters: location.radius_meters,
        is_active: !location.is_active,
      });
      await loadLocations();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update location.');
    } finally {
      setBusyId(null);
    }
  };

  const deleteLocation = async location => {
    if (!window.confirm(`Delete "${location.name}"? Check-in history for this spot will also be removed.`)) {
      return;
    }
    setBusyId(location.id);
    setError('');
    try {
      await apiService.deleteCheckInLocationSuperuser(location.id);
      if (selectedLocationId === location.id) {
        setSelectedLocationId(null);
      }
      if (editingId === location.id) {
        resetForm();
      }
      await loadLocations();
      setNotice('Location deleted.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete location.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const selectedLocation = locations.find(item => item.id === selectedLocationId);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Check-In Locations</h1>
          <p className="staff-page-subheader">
            Set spots where superusers can check in from the mobile app, and see who was there.
          </p>

          {error && (
            <div className="toast toast-error" style={{ marginTop: 16 }}>
              {error}
            </div>
          )}
          {notice && (
            <div className="toast toast-success" style={{ marginTop: 16 }}>
              {notice}
            </div>
          )}

          <div style={{ marginTop: 32, maxWidth: 900 }}>
            <h2 className="staff-section-header">
              {editingId ? 'Edit location' : 'Add location'}
            </h2>

            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Stadium gate, Main office..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Address (optional)</label>
              <input
                className="form-input"
                value={form.address}
                onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">Pick on map</label>
              <p className="staff-page-content" style={{ margin: '0 0 8px' }}>
                Click map to drop pin, or drag pin to adjust. Circle = check-in radius.
              </p>
              <div
                ref={mapElRef}
                style={{
                  width: '100%',
                  height: 380,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#f3f4f6',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <input
                  className="form-input"
                  value={form.latitude}
                  onChange={e => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="37.7749"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input
                  className="form-input"
                  value={form.longitude}
                  onChange={e => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="-122.4194"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Radius (meters)</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={form.radius_meters}
                  onChange={e => setForm(prev => ({ ...prev, radius_meters: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="checkin-active"
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <label htmlFor="checkin-active" className="form-label" style={{ margin: 0 }}>
                Active
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button className="primary-button" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update location' : 'Add location'}
              </button>
              {editingId && (
                <button className="staff-page-button" onClick={resetForm} disabled={saving}>
                  <span className="staff-page-button-text">Cancel</span>
                </button>
              )}
            </div>
          </div>

          <div style={{ marginTop: 48 }}>
            <h2 className="staff-section-header">Locations</h2>
            {locations.length === 0 ? (
              <p className="staff-page-content">No check-in locations yet.</p>
            ) : (
              <div style={{ marginTop: 16, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 8px' }}>Name</th>
                      <th style={{ padding: '10px 8px' }}>Coords</th>
                      <th style={{ padding: '10px 8px' }}>Radius</th>
                      <th style={{ padding: '10px 8px' }}>Status</th>
                      <th style={{ padding: '10px 8px' }}>Check-ins</th>
                      <th style={{ padding: '10px 8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map(location => (
                      <tr
                        key={location.id}
                        style={{
                          borderBottom: '1px solid #f3f4f6',
                          background:
                            selectedLocationId === location.id ? '#f0f9ff' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 600 }}>{location.name}</div>
                          {location.address ? (
                            <div style={{ color: '#6b7280', fontSize: 13 }}>{location.address}</div>
                          ) : null}
                        </td>
                        <td style={{ padding: '12px 8px', fontSize: 13, color: '#4b5563' }}>
                          {location.latitude}, {location.longitude}
                        </td>
                        <td style={{ padding: '12px 8px' }}>{location.radius_meters} m</td>
                        <td style={{ padding: '12px 8px' }}>
                          {location.is_active ? 'Active' : 'Inactive'}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {location.check_in_count ?? 0}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <button
                              className="staff-page-button"
                              onClick={() => setSelectedLocationId(location.id)}
                            >
                              <span className="staff-page-button-text">Who was here</span>
                            </button>
                            <button
                              className="staff-page-button"
                              onClick={() => startEdit(location)}
                              disabled={busyId === location.id}
                            >
                              <span className="staff-page-button-text">Edit</span>
                            </button>
                            <button
                              className="staff-page-button"
                              onClick={() => toggleActive(location)}
                              disabled={busyId === location.id}
                            >
                              <span className="staff-page-button-text">
                                {location.is_active ? 'Deactivate' : 'Activate'}
                              </span>
                            </button>
                            <button
                              className="staff-page-button"
                              onClick={() => deleteLocation(location)}
                              disabled={busyId === location.id}
                            >
                              <span className="staff-page-button-text">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedLocationId && (
            <div style={{ marginTop: 48 }}>
              <h2 className="staff-section-header">
                Who checked in{selectedLocation ? ` — ${selectedLocation.name}` : ''}
              </h2>
              <p className="staff-page-content">Successful check-ins only.</p>
              {checkInsLoading ? (
                <p style={{ color: '#666' }}>Loading check-ins...</p>
              ) : checkIns.length === 0 ? (
                <p className="staff-page-content">No successful check-ins yet.</p>
              ) : (
                <div style={{ marginTop: 16, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 8px' }}>Name</th>
                        <th style={{ padding: '10px 8px' }}>User ID</th>
                        <th style={{ padding: '10px 8px' }}>Email</th>
                        <th style={{ padding: '10px 8px' }}>School</th>
                        <th style={{ padding: '10px 8px' }}>Distance</th>
                        <th style={{ padding: '10px 8px' }}>When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checkIns.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '12px 8px' }}>
                            {item.user?.display_name || '—'}
                          </td>
                          <td
                            style={{
                              padding: '12px 8px',
                              fontSize: 12,
                              color: '#4b5563',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              maxWidth: 180,
                              overflowWrap: 'anywhere',
                            }}
                            title={item.user?.id || ''}
                          >
                            {item.user?.id || '—'}
                          </td>
                          <td style={{ padding: '12px 8px', color: '#4b5563' }}>
                            {item.user?.email || '—'}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            {item.user?.active_school?.name || '—'}
                            {item.user?.active_school?.id ? (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: '#9ca3af',
                                  marginTop: 2,
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {item.user.active_school.id}
                              </div>
                            ) : null}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            {formatDistance(item.distance_meters)}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            {formatDateTime(item.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserCheckInLocations;
